import { useState, useEffect } from 'react';
import { Navbar } from '../../../components/feature/Navbar';
import { Footer } from '../../../components/feature/Footer';
import { isSuspiciousSubmission, addTrackingToForm } from '../../../utils/tracking';

export default function StarterWebsitePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    message: '',
    honeypot: '',
    mathAnswer: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [mathQuestion, setMathQuestion] = useState({ num1: 0, num2: 0, answer: 0 });
  const [formErrors, setFormErrors] = useState({ email: '' });

  const blockedDomains = [
    'test.com', 'fake.com', 'example.com', 'temp-mail.com', 'tempmail.com',
    'throwaway.email', '10minutemail.com', 'guerrillamail.com', 'mailinator.com',
    'trashmail.com', 'yopmail.com', 'fakeinbox.com', 'maildrop.cc'
  ];

  const validateEmail = (email: string): { valid: boolean; error: string } => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (!emailRegex.test(email)) {
      return { valid: false, error: 'Please enter a valid email address' };
    }

    const domain = email.split('@')[1].toLowerCase();

    if (blockedDomains.includes(domain)) {
      return { valid: false, error: 'Please use a valid business or personal email address' };
    }

    const commonTypos: Record<string, string> = {
      'gmial.com': 'gmail.com',
      'gmai.com': 'gmail.com',
      'yahooo.com': 'yahoo.com',
      'yaho.com': 'yahoo.com',
      'hotmial.com': 'hotmail.com',
      'outlok.com': 'outlook.com'
    };

    if (commonTypos[domain]) {
      return { valid: false, error: `Did you mean ${email.split('@')[0]}@${commonTypos[domain]}?` };
    }

    return { valid: true, error: '' };
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setFormData({ ...formData, email });

    if (email) {
      const validation = validateEmail(email);
      setFormErrors({ ...formErrors, email: validation.error });
    } else {
      setFormErrors({ ...formErrors, email: '' });
    }
  };

  useEffect(() => {
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);

    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);

    const fontLink = document.createElement('link');
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@700;800&family=Dancing+Script:wght@400;700&display=swap';
    fontLink.rel = 'stylesheet';
    document.head.appendChild(fontLink);

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', 'https://businesswebsitepro.com/services/starter-website');

    document.title = 'Starter Website Design Services | Affordable Small Business Websites | Vancouver, Toronto, Montreal | BusinessWebsitePro';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional starter website design services for small businesses across Canada, USA, and worldwide. Mobile-responsive, SEO-optimized websites starting at $399. Perfect for startups, local businesses, and entrepreneurs in Vancouver, Toronto, Montreal, Calgary, and beyond.');
    }

    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setMathQuestion({ num1, num2, answer: num1 + num2 });

    return () => {
      document.head.removeChild(preconnect1);
      document.head.removeChild(preconnect2);
      document.head.removeChild(fontLink);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    if (formData.honeypot) {
      console.log('Bot detected via honeypot');
      setIsSubmitting(false);
      return;
    }

    if (parseInt(formData.mathAnswer) !== mathQuestion.answer) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      alert('Incorrect answer to the math question. Please try again.');
      return;
    }

    const suspicionCheck = isSuspiciousSubmission();
    if (suspicionCheck.suspicious) {
      setIsSubmitting(false);
      alert(suspicionCheck.reason);
      return;
    }

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.valid) {
      setFormErrors({ ...formErrors, email: emailValidation.error });
      setIsSubmitting(false);
      return;
    }

    try {
      const formElement = e.target as HTMLFormElement;
      await addTrackingToForm(formElement);

      const submissionData: any = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        businessName: formData.businessName,
        message: formData.message,
        service: 'Starter Website Design'
      };

      const trackingFields = formElement.querySelectorAll('[data-tracking-field]');
      trackingFields.forEach((field: any) => {
        submissionData[field.name] = field.value;
      });

      let telegramSuccess = false;
      let formSuccess = false;

      try {
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
        if (supabaseUrl) {
          const telegramResponse = await fetch(`${supabaseUrl}/functions/v1/telegram-notify`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(submissionData)
          });

          if (telegramResponse.ok) {
            telegramSuccess = true;
          }
        }
      } catch (telegramError) {
        console.error('Telegram notification error:', telegramError);
      }

      try {
        const formBody = new URLSearchParams();
        Object.entries(submissionData).forEach(([key, value]) => {
          formBody.append(key, String(value));
        });

        const formResponse = await fetch('https://readdy.ai/api/form/d4qahv2r20r5k3vfkofg', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString()
        });

        if (formResponse.ok) {
          formSuccess = true;
        }
      } catch (formError) {
        console.error('Form storage error:', formError);
      }

      if (telegramSuccess || formSuccess) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          businessName: '',
          message: '',
          honeypot: '',
          mathAnswer: ''
        });
        
        const num1 = Math.floor(Math.random() * 10) + 1;
        const num2 = Math.floor(Math.random() * 10) + 1;
        setMathQuestion({ num1, num2, answer: num1 + num2 });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Starter Website Design Services",
          "description": "Professional starter website design services for small businesses. Mobile-responsive, SEO-optimized websites perfect for startups and local businesses.",
          "provider": {
            "@type": "LocalBusiness",
            "name": "BusinessWebsitePro",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Vancouver",
              "addressRegion": "BC",
              "addressCountry": "CA"
            }
          },
          "areaServed": ["Canada", "United States", "Worldwide"],
          "serviceType": "Website Design"
        })}
      </script>
      
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/599b4ac4b4e5513afa86cc648f3a89b1.jpg)'
          }}
        />
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
            Starter Website Design Services
          </h1>
          <p className="text-xl sm:text-2xl text-white/90 mb-10 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] max-w-3xl mx-auto">
            Professional, affordable websites perfect for small businesses just starting their online journey
          </p>
          
          {/* CTA Button */}
          <div className="flex justify-center">
            <a
              href="/contact"
              className="bg-teal-500 hover:bg-teal-600 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-line mr-2"></i>
              Book Free Consultation
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Get Your Starter Website</h2>
              
              <form id="starter-website-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleInputChange}
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleEmailChange}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm ${
                      formErrors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="john@example.com"
                  />
                  {formErrors.email && (
                    <p className="text-xs text-red-600 mt-1 flex items-center">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formErrors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="+1 (604) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="businessName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    id="businessName"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Your Business Name"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Details *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your project..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6">
                  <label htmlFor="mathAnswer" className="block text-sm font-semibold text-gray-900 mb-3">
                    <i className="ri-shield-check-line text-yellow-600 mr-2"></i>
                    Security Check: What is {mathQuestion.num1} + {mathQuestion.num2}? *
                  </label>
                  <input
                    type="number"
                    id="mathAnswer"
                    name="mathAnswer"
                    value={formData.mathAnswer}
                    onChange={handleInputChange}
                    required
                    className="w-full md:w-32 px-4 py-3 border-2 border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm font-semibold"
                    placeholder="Answer"
                  />
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 flex items-center gap-3">
                    <i className="ri-check-line text-2xl text-green-600"></i>
                    <p className="text-green-800 font-semibold">Message sent successfully! We'll respond within 24 hours.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 flex items-center gap-3">
                    <i className="ri-error-warning-line text-2xl text-red-600"></i>
                    <p className="text-red-800 font-semibold">Something went wrong. Please try again.</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 flex items-center justify-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Get Started Today
                    </>
                  )}
                </button>
              </form>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose Our Starter Website?</h2>
              <p className="text-lg text-gray-600 mb-8">
                Our starter website design service is perfect for small businesses, startups, and entrepreneurs who need a professional online presence without breaking the bank. Get a fully functional, mobile-responsive website that helps you establish credibility and reach more customers.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-smartphone-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Mobile-Responsive Design</h3>
                    <p className="text-gray-600">Your website will look perfect on all devices - smartphones, tablets, and desktops. With over 60% of web traffic coming from mobile devices, this is essential for reaching your customers.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-search-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">SEO Optimized</h3>
                    <p className="text-gray-600">We build your website with search engine optimization in mind from day one. This helps your business get found by potential customers searching for your services in Vancouver, Toronto, Montreal, and beyond.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-speed-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Fast Loading Speed</h3>
                    <p className="text-gray-600">Speed matters. Our websites are optimized for lightning-fast loading times, which improves user experience and helps with search engine rankings.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-send-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Contact Form Integration</h3>
                    <p className="text-gray-600">Make it easy for customers to reach you with a professional contact form that sends inquiries directly to your email.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What's Included in Your Starter Website</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to establish a professional online presence for your small business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-file-list-3-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">1-3 Pages</h3>
              <p className="text-gray-600">
                Perfect for showcasing your business essentials: Home page, About page, and Contact page. Everything you need to get started online.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-palette-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Custom Design</h3>
              <p className="text-gray-600">
                Your website will be designed to match your brand identity with custom colors, fonts, and layouts that reflect your business personality.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-share-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Social Media Integration</h3>
              <p className="text-gray-600">
                Connect your social media profiles to your website, making it easy for visitors to follow and engage with your business on Facebook, Instagram, and more.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-map-pin-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Google Maps Integration</h3>
              <p className="text-gray-600">
                Help customers find your physical location with an embedded Google Maps display showing your business address and directions.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-global-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Cross-Browser Compatible</h3>
              <p className="text-gray-600">
                Your website will work flawlessly on all major browsers including Chrome, Firefox, Safari, and Edge, ensuring all visitors have a great experience.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-bar-chart-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Basic Analytics Setup</h3>
              <p className="text-gray-600">
                Track your website visitors and understand how people find and use your site with Google Analytics integration included.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Perfect For These Businesses</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our starter website design service is ideal for various types of small businesses across Canada, USA, and worldwide
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-blue-50 rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-store-2-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Local Shops</h3>
              <p className="text-gray-600">Retail stores, boutiques, and specialty shops looking to expand their reach online.</p>
            </div>

            <div className="bg-blue-50 rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-restaurant-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Restaurants &amp; Cafes</h3>
              <p className="text-gray-600">Food businesses wanting to showcase their menu and attract more customers.</p>
            </div>

            <div className="bg-blue-50 rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-star-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Service Providers</h3>
              <p className="text-gray-600">Consultants, freelancers, and professionals offering specialized services.</p>
            </div>

            <div className="bg-blue-50 rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-rocket-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Startups</h3>
              <p className="text-gray-600">New businesses launching their brand and establishing online credibility.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Simple Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From consultation to launch, we make getting your website online easy and stress-free
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Consultation</h3>
              <p className="text-gray-600">
                We discuss your business goals, target audience, and design preferences to understand your vision.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-teal-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Design</h3>
              <p className="text-gray-600">
                Our designers create a custom website layout that reflects your brand and appeals to your customers.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-teal-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Development</h3>
              <p className="text-gray-600">
                We build your website with clean code, ensuring it's fast, secure, and optimized for search engines.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-teal-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                4
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Launch</h3>
              <p className="text-gray-600">
                After your approval, we launch your website and provide training on how to manage your content.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Join hundreds of satisfied small business owners across Canada, USA, and worldwide who trust BusinessWebsitePro for their online presence. Let's build your professional website today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-teal-500 hover:bg-teal-600 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-line mr-2"></i>
              Book Free Consultation
            </a>
            <a
              href="/contact"
              className="bg-white hover:bg-gray-50 text-teal-600 font-semibold py-4 px-8 rounded-lg border-2 border-teal-500 transition-all duration-300 inline-flex items-center justify-center whitespace-nowrap cursor-pointer"
            >
              <i className="ri-question-line mr-2"></i>
              Have Questions?
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}